import type { Messages } from './pt-BR.js';

export const es: Messages = {
  common: {
    save: 'Guardar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    back: 'Volver',
    add: 'Agregar',
    remove: 'Quitar',
    edit: 'Editar',
    confirm: 'Confirmar',
    loading: 'Cargando',
    retry: 'Intentar de nuevo',
    refresh: 'Actualizar',
    actions: 'Acciones',
    details: 'Detalles',
    none: 'Ninguno',
    na: 'n/d',
    yes: 'Sí',
    no: 'No',
    all: 'Todos',
    optional: 'opcional',
    readOnly: 'Solo lectura',
    loadError: 'No se pudo cargar.',
    correlation: 'Código para soporte: {{id}}',
    saved: 'Guardado.',
    required: 'Obligatorio.',
    unsaved: 'Hay cambios sin guardar. ¿Salir de todos modos?',
    unsavedBadge: 'Cambios sin guardar',
  },
  errors: {
    network: 'Sin conexión con el servicio. Revisa la red e intenta de nuevo.',
    forbidden: 'Tu rol no permite esta acción.',
    conflict: 'Otra persona guardó antes. Los datos se recargaron; vuelve a hacer el cambio.',
    session: 'Tu sesión terminó. Vuelve a entrar.',
    invalidResponse: 'El servicio respondió en un formato inesperado. Actualiza el panel; si sigue, abre un ticket.',
    generic: 'No se pudo completar.',
  },
  nav: {
    label: 'Menú principal',
    skip: 'Saltar al contenido',
    overview: 'Resumen',
    connections: 'Conexiones',
    clients: 'Apps',
    users: 'Usuarios',
    collapse: 'Contraer menú',
    expand: 'Expandir menú',
    groups: {
      context: 'Contexto',
      channels: 'Canales',
      audit: 'Auditoría',
      admin: 'Administración',
    },
    chat: 'Chat en el navegador',
    teams: 'Ask en Teams',
    mcp: 'Conectores (Claude, GPT)',
    audit: 'Consultas a Qlik',
    conversations: 'Conversaciones',
    appearance: 'Apariencia',
    settings: 'Configuración',
    ownerNotice: 'Como dueño de asunto, ve sus apps, con la calidad de cada una, y las Conversaciones sobre ellas.',
  },
  user: {
    menu: 'Cuenta y preferencias',
    theme: 'Tema',
    themeSystem: 'Seguir el sistema',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    language: 'Idioma',
    logout: 'Salir',
    expires: 'Sesión hasta {{time}}',
    admin: 'Administrador',
    owner: 'Dueño de tema',
  },
  login: {
    title: 'Entrar al panel',
    subtitle: 'Usa la cuenta de la organización. Lo que ves depende de tu rol en el IdP.',
    sso: 'Entrar con la cuenta de la organización',
    dev: 'Entrar con token de desarrollo',
    devHelp: 'Genéralo con lastrouter dev-token. Solo funciona en instalaciones de desarrollo.',
    token: 'Token',
    submit: 'Entrar',
    refused: 'Token rechazado. Genera otro e intenta de nuevo.',
    footer: 'Lastrouter: capa de contexto entre los agentes de IA y las fuentes de datos.',
  },
  overview: {
    title: 'Resumen',
    period: 'Últimos 7 días',
    calls: 'Llamadas',
    users: 'Personas',
    answered: 'Respondidas',
    clarifications: 'Pidieron aclaración',
    errors: 'Errores',
    negative: 'Comentarios negativos',
    license: 'Licencia',
    publication: 'Catálogo publicado',
    noPublication: 'Aún no hay publicación. Ejecuta lastrouter reload o publica un borrador.',
    connections: 'Conexiones',
    pending: 'Pendientes',
    openDrafts: 'Borradores esperando aprobación: {{count}}',
    openFeedback: 'Comentarios abiertos: {{count}}',
    nothingPending: 'Nada pendiente.',
    publishedBy: 'Publicado por {{by}} el {{at}}',
    commit: 'Commit {{commit}}',
    noCommit: 'Sin commit de Git',
  },
  license: { valida: 'Válida', tolerancia: 'En tolerancia', vencida: 'Vencida', ausente: 'Sin licencia', invalida: 'Inválida', until: 'Hasta {{date}}' },
  connections: {
    title: 'Conexiones',
    subtitle: 'Fuentes de datos conectadas a Lastrouter. Las del archivo de configuración solo las cambia TI.',
    id: 'Conexión',
    type: 'Tipo',
    target: 'Dirección',
    state: 'Estado',
    checked: 'Verificada el',
    managedBy: 'Gestionada por',
    arquivo: 'Archivo',
    painel: 'Panel',
    ok: 'Conectada',
    failed: 'Con falla',
    test: 'Probar como usuario',
    testTitle: 'Probar {{id}}',
    testHelp: 'Escribe el usuario como lo conoce la fuente (ej.: CLUSTER\\maria). La prueba abre cada app como esa persona, sin leer valores.',
    user: 'Usuario de la fuente',
    subject: 'Tema',
    allSubjects: 'Todos los temas',
    run: 'Probar',
    summary: 'El usuario {{user}} abre {{ok}} de {{total}} app(s).',
    app: 'App',
    appTitle: 'Título',
    lastReload: 'Última recarga',
    empty: 'No hay fuentes configuradas.',
  },
  context: {
    title: 'Contexto',
    subtitle: 'Qué significa cada métrica, en qué fuente buscar y qué consultas están validadas.',
    tabPublished: 'Publicado',
    tabDrafts: 'Borradores',
    tabHistory: 'Historial',
    noCatalog: 'Aún no hay catálogo publicado.',
    export: 'Exportar zip',
    import: 'Importar zip',
    newDraft: 'Nuevo borrador',
    source: 'Fuente',
    app: 'App',
    owners: 'Dueños',
    business: 'Negocio',
    technical: 'Técnico',
    metrics: 'Métricas',
    dimensions: 'Dimensiones',
    queries: 'Consultas validadas',
    playbooks: 'Guías de análisis',
    unit: 'Unidad',
    expression: 'Expresión',
    synonyms: 'Sinónimos',
    field: 'Campo',
    type: 'Tipo',
    sensitive: 'Dato personal',
    certified: 'Certificada',
    notCertified: 'Sin certificación',
    version: 'v{{v}}',
    params: 'Parámetros',
    required: 'obligatorio',
    optionalParam: 'opcional',
    groupBy: 'Agrupa por',
    when: 'Cuándo usar',
    steps: 'Pasos',
    dimTypes: { texto: 'Texto', periodo_mensal: 'Mes (AAAA-MM)', ano: 'Año', data: 'Fecha (período derivado)' },
    history: {
      published: 'Publicada el',
      by: 'Por',
      commit: 'Commit',
      reason: 'Motivo',
      changes: 'Cambios',
      active: 'Activa',
      restore: 'Volver a publicar esta versión',
      restoreTitle: 'Volver a publicar {{id}}',
      restoreHelp: 'Devuelve el catálogo a esta versión. Se convierte en una publicación nueva en el historial.',
      restored: 'Versión publicada de nuevo como {{id}}.',
      restoredFrom: 'vuelta a {{id}}',
      dirty: 'con cambios fuera del commit',
    },
    drafts: {
      empty: 'No hay borradores. Crea uno a partir del catálogo publicado.',
      reason: 'Motivo',
      author: 'Autor',
      created: 'Creado el',
      state: 'Estado',
      subjects: 'Temas cambiados',
      status: { aberto: 'Abierto', aprovado: 'Aprobado', publicado: 'Publicado', descartado: 'Descartado' },
      newTitle: 'Nuevo borrador',
      newHelp: 'El borrador empieza igual al catálogo publicado. El motivo aparece en el historial y para los dueños que van a aprobar.',
      create: 'Crear borrador',
      open: 'Abrir',
      importTitle: 'Importar zip',
      importHelp: 'Usa un zip de la carpeta de contexto (mismo formato del repositorio). Se convierte en un borrador; nada se publica sin aprobación.',
      file: 'Archivo zip',
      imported: 'Borrador creado a partir del zip.',
    },
    editor: {
      title: 'Borrador {{id}}',
      backToDrafts: 'Volver a los borradores',
      readOnly: 'Borrador {{status}}: solo lectura.',
      changes: 'Qué cambia',
      noChanges: 'Sin cambios respecto al catálogo publicado.',
      approvals: 'Aprobaciones',
      approvalsHelp: 'Cada tema cambiado necesita la aprobación del dueño de negocio y del dueño técnico.',
      pending: 'Pendiente: {{owner}}',
      approvedBy: 'Aprobado por {{by}}',
      refusedBy: 'Rechazado por {{by}}',
      approvedInPlace: 'Aprobado por {{by}} en nombre de {{owner}}',
      refusedInPlace: 'Rechazado por {{by}} en nombre de {{owner}}',
      substitute: 'Decidir en nombre del responsable',
      substituteHelp: '{{owner}} no puede decidir. Como admin, usted decide en su nombre. La decisión queda en el historial con su nombre y genera una alerta de seguridad.',
      reason: 'Motivo',
      approve: 'Aprobar',
      refuse: 'Rechazar',
      comment: 'Comentario',
      decisionTitle: '{{decision}} como dueño {{papel}} de {{subject}}',
      papelNegocio: 'de negocio',
      papelTecnico: 'técnico',
      publish: 'Publicar',
      publishHelp: 'Publica cuando los dos dueños de cada tema cambiado aprueben.',
      published: 'Publicado como {{id}}.',
      discard: 'Descartar',
      discardConfirm: '¿Descartar este borrador? Ya no se podrá publicar.',
      discarded: 'Borrador descartado.',
      validation: 'Validación',
      valid: 'Catálogo válido.',
      invalid: 'Corrige antes de guardar:',
      validating: 'Revisando',
      save: 'Guardar borrador',
      saveHelp: 'Guardar borra las aprobaciones ya dadas.',
      savedDraft: 'Borrador guardado.',
      reasonLabel: 'Motivo del cambio',
      subject: 'Tema',
      ready: 'Listo para publicar.',
      refusedState: 'Un rechazo bloquea la publicación hasta que el borrador cambie.',
    },
    form: {
      id: 'Id',
      idHelp: 'Minúsculas, números, _ y -.',
      name: 'Nombre',
      description: 'Descripción',
      synonymsHelp: 'Separa con comas.',
      unitHelp: 'Código de moneda (BRL), percentual o texto (contribuyentes).',
      expressionHelp: 'En el lenguaje de la fuente, con al menos una agregación. Sin $( ).',
      fieldHelp: 'Nombre del campo en la fuente.',
      sensitiveHelp: 'Queda fuera de la lista de valores y de la LLM, y sale enmascarado.',
      example: 'Pregunta de ejemplo',
      exampleHelp: 'Sin datos personales: este texto va a la LLM.',
      queryMetrics: 'Métricas (ids, separados por coma)',
      queryGroupBy: 'Agrupar por (ids de dimensión, separados por coma)',
      param: 'Parámetro',
      paramDimension: 'Dimensión',
      paramRequired: 'Obligatorio',
      certifiedBy: 'Certificada por',
      when: 'Cuándo usar',
      steps: 'Pasos (uno por línea)',
      addMetric: 'Agregar métrica',
      addDimension: 'Agregar dimensión',
      addQuery: 'Agregar consulta',
      addParam: 'Agregar parámetro',
      addPlaybook: 'Agregar guía',
      editSubject: 'Editar tema',
      apply: 'Aplicar al borrador',
      applied: 'Cambios aplicados al borrador. Guarda para registrarlos.',
      fixErrors: 'Corrige los campos marcados.',
    },
  },
  apps: {
    search: 'Buscar',
    searchPlaceholder: 'Nombre, id en Qlik, espacio, tema o cliente',
    client: 'Cliente',
    anyClient: 'Todos los clientes',
    noClient: 'Sin cliente',
    source: 'Fuente',
    accessUnavailable: 'No se pudo saber ahora qué apps abre en Qlik, así que la lista quedó vacía. Intente de nuevo en unos minutos.',
    emptyMine: 'No abre ninguna app en Qlik. Pida acceso al administrador de Qlik.',
    anySource: 'Todas las fuentes',
    sort: 'Orden',
    sorts: {
      name: 'Nombre (A a Z)',
      desc_name: 'Nombre (Z a A)',
      status: 'Estado',
      client: 'Cliente',
      desc_last_reload: 'Recarga más reciente',
    },
    clear: 'Limpiar filtros',
    statusFilter: 'Filtrar por estado',
    all: 'Todas',
    statusFilters: {
      published: 'Publicadas',
      draft: 'En borrador',
      not_enabled: 'No habilitadas',
      missing: 'Desaparecieron de la fuente',
    },
    name: 'App',
    status: 'Estado',
    version: 'Versión',
    liveVersion: 'v{{n}} activa',
    draftVersion: 'borrador v{{n}}',
    caption: 'Apps: {{count}}',
    select: 'Seleccionar {{name}}',
    selectPage: 'Seleccionar todas de esta página',
    reloadedAt: 'Recarga: {{when}}',
    noMatchFor: 'Ninguna app con "{{q}}" y estos filtros.',
    noMatch: 'Ninguna app con estos filtros.',
    empty: 'Aún no hay apps registradas. Usa "Actualizar lista de Qlik" para traer las apps que ves en Qlik.',
    emptyOwner: 'Aún no eres dueño de ninguna app.',
    range: '{{from}} a {{to}} de {{total}}',
    perPage: 'Por página',
    prev: 'Anterior',
    next: 'Siguiente',
    pageOf: 'Página {{page}} de {{pages}}',
    pagination: 'Páginas de la lista',
    back: 'Volver a la lista de apps',
    unlinkOne: 'Quitar {{name}} del cliente',
    bulk: {
      label: 'Acciones en las apps seleccionadas',
      selected: 'Seleccionadas: {{count}}',
      setClient: 'Vincular a un cliente',
      moveClient: 'Cambiar de cliente',
      unlink: 'Quitar del cliente',
      enable: 'Habilitar',
      disable: 'Apagar',
      clearSelection: 'Limpiar selección',
      client: 'Cliente',
      noClient: 'Sin cliente (quitar del cliente)',
      andMore: 'y {{count}} más',
      done: 'Listo. Apps cambiadas: {{count}}.',
      partial: 'Cambiadas: {{changed}}. Fallaron {{failed}}:',
      set_clientTitle: 'Vincular a un cliente',
      set_clientHelp: 'Las apps pasan a pertenecer al cliente elegido. El contexto de cada una no cambia.',
      set_clientConfirm: 'Vincular ({{count}})',
      set_clientNothing: 'Ninguna app seleccionada.',
      unlinkTitle: 'Quitar del cliente',
      unlinkHelp: 'La app sigue registrada, con el contexto intacto; solo deja de pertenecer al cliente. El modo privacidad del cliente deja de valer para ella.',
      unlinkConfirm: 'Quitar ({{count}})',
      unlinkNothing: 'Ninguna de las seleccionadas tiene cliente.',
      unlinkSkipped: '{{count}} ya están sin cliente y quedan como están.',
      enableTitle: 'Habilitar apps',
      enableHelp: 'Cada app recibe un borrador de contexto vacío para generar y revisar. Nada llega a los canales antes de publicar.',
      enableConfirm: 'Habilitar ({{count}})',
      enableNothing: 'Todas las seleccionadas ya están habilitadas.',
      enableSkipped: '{{count}} ya están habilitadas y quedan como están.',
      disableTitle: 'Apagar apps',
      disableHelp: 'El borrador abierto se descarta y la versión publicada se archiva. El historial se guarda.',
      disableConfirm: 'Apagar ({{count}})',
      disableNothing: 'Ninguna de las seleccionadas está habilitada.',
      disableSkipped: '{{count}} ya están apagadas y quedan como están.',
      disablePublished: '{{count}} publicadas salen de los canales ahora.',
    },
  },
  clients: {
    title: 'Clientes',
    subtitle: 'Las apps de Qlik que usted abre, con el contexto que cada una da al chat y a los otros canales.',
    new: 'Nuevo cliente',
    create: 'Crear cliente',
    created: 'Cliente creado.',
    name: 'Nombre',
    context: 'Contexto de la empresa',
    contextHelp: 'Lo que el agente necesita saber de la empresa en todas sus apps: qué hace, siglas, público y reglas generales. Admite markdown.',
    privacy: 'Modo privacidad',
    privacyHelp: 'Los números de las apps de este cliente no van a la IA: sabe qué se consultó, y los valores aparecen solo para la persona, en la tabla y el gráfico debajo de la respuesta. En Claude y otros clientes MCP, los valores no salen.',
    apps: 'Apps',
    appsCount: '{{published}} publicadas de {{total}}',
    empty: 'Todavía no hay clientes. Crea el primero para vincularle apps.',
    myApps: 'Apps',
    myAppsHelp: 'Apps cuyo contexto puedes editar o aprobar.',
    noApps: 'Este cliente no tiene apps.',
    back: 'Volver a los clientes',
    export: 'Exportar',
    soon: 'Próximamente',
    delete: 'Eliminar cliente',
    deleteConfirm: '¿Eliminar {{name}}? Sus apps quedan sin cliente, con el contexto intacto.',
    deleted: 'Cliente eliminado.',
    tabApps: 'Apps',
    tabContext: 'Contexto de la empresa',
    saved: 'Contexto de la empresa guardado.',
    sync: 'Actualizar lista de Qlik',
    syncTitle: 'Actualizar lista de apps',
    syncHelp: 'Lista las apps de la fuente con tu usuario y actualiza el registro. Las apps nuevas entran sin cliente.',
    source: 'Fuente',
    noSources: 'No hay conexiones configuradas.',
    runSync: 'Actualizar',
    syncResult: 'Listado como {{user}}: {{added}} nuevas, {{updated}} actualizadas, {{missing}} ya no están en la fuente.',
    addApp: 'Agregar apps',
    addAppTitle: 'Agregar app a {{name}}',
    addAppHelp: 'Busca y marca las apps sin cliente. Si una app no aparece, actualiza la lista de Qlik.',
    noUnassigned: 'No hay apps sin cliente. Actualiza la lista de Qlik.',
    titleBoth: 'Clientes y apps',
    allApps: 'Todas las apps',
    search: 'Buscar cliente',
    noMatch: 'Ningún cliente con "{{q}}".',
    unassignedLink: 'Apps sin cliente: {{count}}',
    publishedShare: 'Publicadas',
    appsAdded: 'Apps vinculadas al cliente: {{count}}.',
    addApps: 'Agregar ({{count}})',
    refineSearch: 'Se muestran las primeras 50 de {{count}}. Refina la búsqueda para encontrar la app.',
  },
  assets: {
    batteryTabs: { run: 'Ejecución', answerKey: 'Respuestas esperadas', certification: 'Certificación' },
    batteryRun: {
      trendShort: '+{{improved}} −{{regressed}} desde la anterior',
      criticalShort: 'críticas {{score}}%',
      costLabel: 'Costo de la ejecución',
      aboutSuggestions: 'Sobre las mejoras',
      aboutBattery: 'Sobre la batería',
      aboutLearn: 'Sobre Aprender solo',
      suggestionsShort: 'Mejoras ({{count}} sin decidir)',
      why: 'Por qué',
      noRuns: 'Aún no hay ejecuciones. Haga clic en Ejecutar.',
      runsCaption: 'Ejecuciones de la batería',
      runWhen: 'Ejecución',
      runScore: 'Nota',
      runQuestions: 'Preguntas',
      runTrend: 'Cambió',
      cycleShort: 'ciclo {{cycle}}/{{cycles}}',
      backToRuns: 'Ejecuciones',
      runOf: 'Ejecución del {{when}}',
      tokens: 'LLM: {{calls}} llamadas, {{input}} mil tokens de entrada y {{output}} mil de salida',
      validatedQuery: 'Consulta validada',
      alsoAsked: 'también preguntan',
      verified: 'verificada',
      revert: 'Deshacer',
      reverted: 'Mejora deshecha. Ejecute la batería de nuevo para comprobar.',
      learn: 'Aprender solo',
      learnHelp: 'Ejecuta la batería, lleva al borrador solo las mejoras verificadas (fórmula igual a la esperada) y ejecuta de nuevo, hasta 3 ciclos u 80%. Nada se publica solo.',
      trend: '{{improved}} mejoraron · {{regressed}} empeoraron desde la ejecución anterior',
      cycle: 'ciclo {{cycle}} de {{cycles}}',
      trends: { melhorou: 'mejoró', piorou: 'empeoró', igual: 'igual' },
      publishOne: 'Publicar esta',
      refuseOne: 'Rechazar',
      publishTitle: '¿Publicar esta mejora ahora?',
      publishHelp: 'Entra en el contexto de la app y vale en el chat al instante. Como omite la aprobación de los dueños, queda registrada en la auditoría con el motivo.',
      draftHasOther: 'La app tiene un borrador abierto con otros cambios: se publicarán junto.',
      publishNow: 'Publicar ahora',
      publishing: 'Publicando...',
      published: 'Mejora publicada. Ejecute la batería de nuevo para ver el efecto.',
      history: 'Ejecución',
      historyOption: '{{when}} · nota {{score}} · {{state}}',
      ok: 'OK (mínimo {{min}}%)',
      belowMin: 'Por debajo de {{min}}%',
      newMeasure: 'Medida nueva',
      synonyms: 'sinónimos',
      suggestions: 'Mejoras sugeridas ({{count}} sin decidir)',
      suggestionsHelp: 'Cada pregunta incorrecta genera una sugerencia. Las marcadas como verificadas usan la fórmula esperada y no necesitan revisión; las de texto, sí. Llévelas al borrador o publíquelas una a una, y ejecute la batería de nuevo: prueba el borrador y muestra qué mejoró o empeoró.',
      noSuggestions: 'No hay sugerencias en esta ejecución.',
      pickSuggestion: 'Elegir la sugerencia de la pregunta {{id}}',
      pickAll: 'Marcar todas sin decidir',
      toDraft: 'Llevar al borrador ({{count}})',
      refuse: 'Rechazar ({{count}})',
      applied: '{{count}} mejoras llevadas al borrador. Ejecute la batería de nuevo para ver el efecto.',
      draftReason: 'Mejoras sugeridas por la batería',
      state: { aplicada: 'En el borrador', publicada: 'Publicada', recusada: 'Rechazada', revertida: 'Deshecha' },
      archiveState: { concluida: 'terminada', cancelada: 'cancelada', falhou: 'falló' },
      showQuestions: 'Ver las preguntas ({{count}})',
      noQuery: 'No armó consulta',
      noFormulaYet: 'Aún no preguntada',
      agentFormula: 'Fórmula del agente',
      run: 'Ejecutar',
      search: 'Buscar',
      searchPlaceholder: 'Pregunta, fórmula, respuesta o id',
      perPage: 'Por página',
      onlyOne: 'Mostrando solo la pregunta {{id}}.',
      showAll: 'Ver todas',
      noMatch: 'Ninguna pregunta coincide con la búsqueda.',
      noAnswerYet: 'Aún sin respuesta',
      noFormula: 'generada del catálogo',
      answered: '{{done}} de {{total}} preguntas hechas',
      tally: '{{right}} correctas · {{wrong}} incorrectas',
      segmentsLabel: '{{right}} correctas y {{wrong}} incorrectas de {{total}} preguntas',
      title: 'Batería de preguntas',
      help: 'El agente recibe cada pregunta y arma la consulta. La batería compara la fórmula armada con la esperada. Nada se calcula en Qlik y no se guarda ninguna respuesta en texto.',
      progress: 'Pregunta {{done}} de {{total}} ({{pct}}%)',
      progressLabel: 'Avance de la batería',
      summary: 'Nota {{score}}%: {{right}} correctas y {{wrong}} incorrectas. Ejecutada el {{when}}.',
      critical: 'Críticas: {{score}}%.',
      notRun: '{{count}} preguntas listas. La batería aún no corrió con ellas.',
      empty: 'Aún no hay preguntas aprobadas. Complete las respuestas esperadas arriba.',
      filter: 'Mostrar',
      question: 'Pregunta',
      expected: 'Fórmula esperada',
      answer: 'Respuesta del agente',
      result: 'Resultado',
      criticalTag: 'crítica',
      pagination: 'Páginas de preguntas de la batería',
      filters: { all: 'Todas ({{count}})', wrong: 'Incorrectas ({{count}})', right: 'Correctas ({{count}})', pending: 'Sin nota aún ({{count}})' },
      results: { 'certo': 'Correcta', 'errado': 'Incorrecta', 'sem resposta': 'Sin respuesta', 'não avaliado': 'No evaluada', 'respondido': 'Respondida', 'aguardando': 'Esperando' },
    },
    answerKey: {
      download: 'Descargar JSON',
      howTitle: 'Formato del archivo y prompt para generarlo con IA',
      formatHelp: 'El archivo sigue el formato {{format}}: fecha de generación (gerado_em, AAAA-MM-DD), convenciones de la app y la lista de pruebas con id, categoría, pregunta, respuesta (la fórmula) y crítico. La planilla usa las mismas columnas. El formato completo está en docs/gabarito.md.',
      privacyTitle: 'Antes de usar el prompt, acuérdelo con el cliente',
      privacy: 'La IA que arma las respuestas lee la app en Qlik: nombres de campos, valores y totales van al proveedor de esa IA. Use solo una IA aprobada por el cliente, con el consentimiento del responsable de los datos.',
      prompt: 'Prompt para una IA conectada a Qlik',
      promptHelp: 'Péguelo en una IA que accede a Qlik (por ejemplo, por MCP). Devuelve el JSON listo para importar aquí.',
      copy: 'Copiar prompt',
      copied: 'Prompt copiado.',
      copyFailed: 'No se pudo copiar. Seleccione el texto y cópielo.',
      generatedAt: 'Respuestas generadas el {{date}}',
      conventions: 'Convenciones de la app',
      category: 'Categoría',
      categoryN: 'Categoría de la pregunta {{n}}',
      title: 'Respuestas esperadas',
      help: 'Preguntas que el agente debe acertar y la fórmula de Qlik que responde cada una. Qlik calcula el valor esperado; nunca se escribe a mano. Cada fila se vuelve un caso aprobado de la batería.',
      import: 'Importar planilla o JSON',
      paste: 'Pegar JSON',
      add: 'Agregar pregunta',
      empty: 'Aún no hay preguntas. Importe una planilla con las columnas Pregunta y Respuesta (la fórmula), un archivo JSON, o agréguelas a mano.',
      question: 'Pregunta',
      formula: 'Respuesta (fórmula de Qlik)',
      critical: 'Crítica',
      questionN: 'Pregunta {{n}}',
      formulaN: 'Fórmula de la pregunta {{n}}',
      criticalN: 'La pregunta {{n}} es crítica',
      removeN: 'Quitar la pregunta {{n}}',
      pagination: 'Páginas de respuestas esperadas',
      saved: 'Guardadas {{count}} preguntas. Las fórmulas nuevas se calcularon en Qlik.',
      saving: 'Guardando y calculando en Qlik...',
      count: '{{count}} preguntas',
      incomplete: '{{count}} filas sin pregunta o sin fórmula no se guardarán',
      loaded: '{{count}} preguntas cargadas. Revise y guarde.',
      warnings: '{{count}} filas ignoradas',
      pasteTitle: 'Pegar las respuestas en JSON',
      pasteHelp: 'Una lista de objetos con pregunta y respuesta (la fórmula). También vale "formula" en lugar de "respuesta" y "critico": true.',
      pasteLoad: 'Cargar',
    },
    name: 'App',
    space: 'Espacio',
    status: 'Estado',
    published: 'Publicada',
    draft: 'Borrador',
    lastReload: 'Última recarga',
    missing: 'Ya no está en la fuente',
    missingHelp: 'La última actualización de la lista no encontró esta app en la fuente: fue eliminada o quien actualizó perdió el acceso.',
    statuses: { nao_habilitado: 'No habilitada', rascunho: 'Borrador', publicado: 'Publicada' },
    versionN: 'v{{n}}',
    qlikId: 'Id en Qlik',
    client: 'Cliente',
    noClient: 'Sin cliente',
    publishedVersion: 'Versión publicada',
    publish: 'Publicar',
    saveFirst: 'Guarda los cambios antes de publicar.',
    publishedOk: 'Versión {{n}} publicada.',
    editContext: 'Editar contexto',
    newDraftTitle: 'Abrir borrador',
    newDraftHelp: 'El borrador parte de la versión publicada, o vacío si no se publicó nada. El motivo aparece en el historial.',
    newDraftFrom: 'El borrador parte de la versión {{n}}. El motivo aparece en el historial.',
    reason: 'Motivo',
    create: 'Abrir borrador',
    draftCreated: 'Borrador v{{n}} abierto.',
    draftExists: 'Ya hay un borrador abierto. Publícalo o descártalo antes.',
    disable: 'Apagar app',
    disableConfirm: '¿Apagar {{name}}? La app sale del catálogo y el chat deja de responder sobre ella. El contexto se conserva.',
    disabled: 'App apagada.',
    generate: {
      againTitle: 'Generar el contexto de nuevo',
      againHelp: 'Lee la app en Qlik otra vez, tarda unos minutos y crea una propuesta nueva para revisar. El contexto publicado no cambia hasta que alguien acepte la propuesta, la apruebe y la publique.',
      notes: 'Lo que sabe sobre esta app',
      notesHelp: 'La generación lee la app en Qlik con su acceso. Si quiere, cuente antes lo que solo sabe quien conoce la app.',
      notesHint: 'Siglas, reglas de negocio, lo que queda fuera, qué números importan. La IA lo usa para escribir el significado de cada ítem; si una nota no coincide con la fórmula, lo avisa y pregunta.',
      images: 'Imágenes',
      imagesHint: 'Hasta {{count}} capturas de pantalla o fotos de documentos. También puede pegar una captura en el campo de texto con Ctrl+V.',
      imagesMax: 'Caben hasta {{count}} imágenes.',
      imageUnreadable: 'No se pudo leer esa imagen. Use PNG, JPEG o WebP.',
      imageN: 'Imagen {{n}}',
      imageRemove: 'Quitar la imagen {{n}}',
      title: 'Generar contexto',
      help: 'Lastrouter lee el modelo de la app en Qlik como usted (tablas, campos, medidas y dimensiones maestras, hojas) y arma una propuesta de contexto con los riesgos que encontró. Nada llega a la app hasta que aplique la propuesta a un borrador.',
      start: 'Generar contexto',
      none: 'Ninguna generación todavía.',
      runs: 'Generaciones',
      runsHint: 'Las otras pestañas muestran la generación elegida aquí.',
      run: 'Generación',
      startedBy: 'Iniciada por {{user}} el {{when}}',
      modelCounts: '{{tables}} tablas, {{fields}} campos, {{measures}} medidas y {{dimensions}} dimensiones maestras, {{sheets}} hojas',
      steps: {
        ler_modelo: 'Leer el modelo en Qlik',
        extrair: 'Leer la app en Qlik',
        planejar: 'Planificar las verificaciones',
        calcular: 'Calcular en el motor de Qlik',
        interpretar: 'Interpretar con IA',
        gerar: 'Armar contexto y propuesta',
        gravar: 'Guardar',
        bateria: 'Batería contra el agente'
      },
      cancel: 'Cancelar',
      cancelled: 'Cancelado. Lo que ya se guardó queda en la ejecución.',
      cancelledTitle: 'Ejecución cancelada',
      runningHint: 'Esta pantalla se actualiza sola. Puede salir: la generación sigue en el servidor.',
      errorTitle: {
        ler_modelo: 'No se pudo leer la app en Qlik',
        extrair: 'No se pudo leer la app en Qlik',
        planejar: 'No se pudieron planificar las verificaciones',
        calcular: 'El motor de Qlik no calculó las verificaciones',
        interpretar: 'La IA no pudo interpretar la app',
        gerar: 'No se pudo armar el contexto',
        bateria: 'La batería contra el agente se detuvo a la mitad',
        other: 'La generación se detuvo con un error'
      },
      errorHelp: 'Verifique que abre esta app en Qlik e intente de nuevo. Si se repite, envíe el texto de abajo a soporte.',
      stepStatus: {
        rodando: 'En curso',
        ok: 'Hecho',
        falhou: 'Falló'
      },
      runStatus: {
        rodando: 'en curso',
        ok: 'lista',
        falhou: 'falló',
        interrompida: 'interrumpida'
      },
      findings: 'Riesgos encontrados ({{n}})',
      findingsHelp: 'Los críticos y altos se vuelven reglas en la sección Restricciones de la propuesta.',
      noFindings: 'Ningún riesgo encontrado en el modelo.',
      severity: {
        critico: 'Crítico',
        alto: 'Alto',
        medio: 'Medio',
        baixo: 'Bajo'
      },
      impact: 'Impacto',
      recommendation: 'Qué hacer',
      risk: {
        accept: 'Aceptar riesgo',
        accepted: 'Aceptado',
        acceptedBy: 'Aceptado por {{by}} el {{when}}: {{reason}}',
        reason: 'Motivo',
        reasonHint: 'Al menos 10 caracteres. Ej.: tabla de selector, suelta a propósito.',
        confirm: 'Aceptar',
        reopen: 'Reabrir',
        acceptedToast: 'Riesgo aceptado. Certificación actualizada.',
        reopenedToast: 'Riesgo reabierto. Certificación actualizada.'
      },
      applied: 'Propuesta aplicada al borrador. Las aprobaciones se reiniciaron.',
      startAgain: 'Generar de nuevo',
      pipeline: {
        resultTitle: 'Resultado',
        resultHelp: 'Lo que Lastrouter encontró en la app y lo que propone. Los números salen del motor de Qlik.',
        risks: 'Riesgos por gravedad',
        riskCount_one: '{{count}} riesgo',
        riskCount_other: '{{count}} riesgos',
        questions: 'Preguntas para el cliente',
        questionsHint: 'respondidas en la planilla',
        ofTotal: '{{n}} de {{total}}',
        catalog: 'Catálogo recomendado',
        catalogHint: 'elementos de la app que el agente debe usar, revisar o no ver',
        expose: 'mostrar',
        review: 'revisar',
        hide: 'ocultar',
        quality: 'Calidad del catálogo',
        qualityHint: 'hoy → con el contexto propuesto',
        numbers: 'Situación de los números',
        numbersHint: 'totales verificados en el motor',
        ai: 'IA usada',
        aiDetail: '{{items}} elementos interpretados, {{removed_evidence}} evidencias sin prueba quitadas, {{rewrites}} descripciones reescritas',
        noAi: 'Sin IA configurada: el contexto salió sin el significado escrito de los elementos.',
        noData: 'sin dato',
        outOfProposal: 'Elementos que quedaron fuera de la propuesta ({{n}})',
        outOfProposalHelp: 'Aparecen en la app, pero Lastrouter no pudo llevarlos al contexto. Trátelos a mano si hace falta.',
        downloadTitle: 'Descargar',
        downloadHelp: 'La planilla va al cliente. Los otros archivos son el contexto en otros formatos.',
        files: {
          checkpoint_xlsx: 'Planilla del cliente',
          contexto_ossie_yaml: 'Contexto en formato Ossie',
          vocabulario_qlik_answers_xlsx: 'Vocabulario de Qlik Answers',
          contexto_json: 'Contexto (JSON)',
          avaliacao_json: 'Descargar la evaluación',
          bateria_json: 'Batería (JSON)',
          snapshot_json: 'Lectura de la app (JSON)'
        },
        validationTitle: 'Validación del cliente',
        validationHelp: 'Descargue la planilla del cliente; él aprueba los casos de prueba, responde las preguntas, decide el catálogo y marca los riesgos, y usted sube aquí el archivo completado.',
        file: 'Planilla completada (.xlsx, hasta 10 MB)',
        send: 'Enviar planilla completada',
        sending: 'Enviando...',
        sent: 'Planilla importada. Contexto, batería y certificación se rehicieron.',
        tooBig: 'El archivo supera 10 MB. Envíe solo la planilla completada.',
        changedTitle: 'Lo que cambió con la planilla',
        changedCases: 'Casos aprobados: {{before}} → {{after}}',
        changedAnswers: 'Preguntas respondidas: {{before}} → {{after}} de {{total}}',
        batteryTitle: 'Batería y certificación',
        batteryHelp: 'La batería hace al agente las preguntas que el cliente aprobó, cada una en una conversación nueva, y compara las respuestas con los números de Qlik. La certificación dice si el contexto puede ir a producción.',
        batteryCounts: '{{cases}} casos en la batería, {{critical}} críticos, {{approved}} aprobados por el cliente.',
        runBattery: 'Correr batería contra el agente',
        noApproved: 'Todavía no hay caso aprobado. El cliente aprueba los casos en la pestaña Batería de la planilla; envíela de vuelta y el botón se habilita.',
        batteryRunning: 'Batería en curso. Tarda unos minutos y la pantalla se actualiza sola.',
        released: 'Liberado para producción',
        blocked: 'Certificación incompleta (no impide publicar)',
        contextVersion: 'contexto {{v}}',
        score: 'Nota general',
        criticalScore: 'Nota de los casos críticos',
        noScore: 'Las notas aparecen después de la primera batería contra el agente.',
        conditionOk: 'cumplida',
        conditionNo: 'no cumplida'
      }
    },
    tabsLabel: 'Etapas del contexto de la app',
    tabs: { summary: 'Resumen', risks: 'Riesgos', context: 'Contexto', validation: 'Validación', battery: 'Batería', publish: 'Publicar', quality: 'Calidad', versions: 'Versiones' },
    tabState: { done: 'hecho', pending: 'pendiente', blocked: 'bloqueado' },
    nextStepTo: 'Próximo paso: {{tab}}',
    nextHint: {
      summary: 'Genera el contexto: Lastrouter lee la app en Qlik y arma una propuesta.',
      risks: 'Mira los riesgos que la generación encontró en el modelo de la app.',
      context: 'Compara la propuesta con lo que vale hoy, sección por sección, y lleva al borrador lo que tenga sentido.',
      validation: 'Descarga la planilla, envíala al cliente y súbela de vuelta completada.',
      battery: 'Ejecuta la batería contra el agente para obtener el sello de liberado.',
      publish: 'Publica con la aprobación de los dueños, o directo, con el motivo registrado.',
      versions: 'Mira las versiones publicadas y qué cambió en cada una.',
    },
    goTo: 'Abrir {{tab}}',
    needsRun: {
      none: 'Esta etapa usa la generación del contexto, que todavía no se ejecutó en esta app.',
      unfinished: 'La última generación no terminó. Mira la situación en Resumen.',
      old: 'Esta generación tiene el formato antiguo, sin planilla del cliente ni batería. Genera de nuevo para tener esta etapa.',
    },
    review: {
      openAll: 'Abrir todas',
      closeAll: 'Cerrar todas',
      today: 'Lo que vale hoy',
      proposal: 'Propuesta de la generación',
      draft: 'Texto nuevo',
      whatChanges: 'Qué cambia',
      diff: { add: 'entra', del: 'sale' },
      emptyText: 'Vacío.',
      tag: { suggested: 'Propuesta nueva', changed: 'Cambió en el borrador', same: 'Sin propuesta' },
      accept: 'Aceptar la propuesta',
      edit: 'Editar',
      keep: 'Mantener lo de hoy',
      runSuggests: 'Ver el texto que propone la generación',
      fromRun: 'Propuesta de la generación del {{when}}, por {{user}}',
      pending_one: '{{count}} parte con propuesta para revisar.',
      pending_other: '{{count}} partes con propuesta para revisar.',
      nothingPending: 'Todo lo que propuso la generación ya está en el contexto.',
      acceptAll: 'Aceptar todas las propuestas',
      saveFirst: 'Guarda los cambios del borrador antes de aceptar una propuesta.',
      draftReason: 'Propuesta de la generación de contexto',
      semanticHelp: 'Métricas, dimensiones, consultas validadas y guiones que el agente usa para consultar Qlik.',
      semanticCounts: '{{metrics}} métricas y {{dimensions}} dimensiones',
      semanticFresh_one: 'La generación propone {{count}} ítem que la app todavía no tiene: {{names}}.',
      semanticFresh_other: 'La generación propone {{count}} ítems que la app todavía no tiene: {{names}}.',
      semanticNoFresh: 'La app ya tiene todas las métricas y dimensiones de la propuesta.',
      addMissing: 'Agregar lo que falta',
      replaceAll: 'Reemplazar por la propuesta',
      editSemantic: 'Editar métricas, dimensiones y consultas',
      seeSemantic: 'Ver métricas, dimensiones y consultas',
    },
    semantic: 'Capa semántica',
    versions: 'Versiones',
    history: 'Historial',
    sections: {
      contexto: 'Contexto',
      regras: 'Reglas de negocio',
      premissas: 'Supuestos',
      restricoes: 'Restricciones y negativos',
      vocabulario: 'Vocabulario',
      sinonimos: 'Sinónimos',
      exemplos: 'Ejemplos y contexto adicional',
      correcoes: 'Correcciones',
      tom: 'Tono de voz',
    },
    sectionHelp: {
      contexto: 'Qué cubre la app, de dónde vienen los datos y a quién sirve.',
      regras: 'Cómo calcular e interpretar los números de esta app.',
      premissas: 'Lo que el agente puede suponer cuando la pregunta no lo dice, como el período o la unidad por defecto.',
      restricoes: 'Lo que el agente no puede hacer ni responder. Es obligatorio cumplirlo.',
      vocabulario: 'Siglas y términos del negocio, con su significado.',
      sinonimos: 'Otros nombres que la gente usa para métricas, dimensiones y valores.',
      exemplos: 'Preguntas de ejemplo y contexto que ayuda a responder.',
      correcoes: 'Correcciones hechas por personas. Prevalecen sobre las reglas.',
      tom: 'Cómo habla el asistente sobre esta app: formal o cercano, términos a evitar, cómo tratar a la persona. Vale por encima del tono general de Apariencia.',
    },
    noDraft: 'Ves la versión publicada. Para cambiarla, haz clic en Editar en la sección, escribe y guarda. El cambio queda aparte y solo llega al chat después de aprobado y publicado.',
    nothing: 'Esta app aún no está habilitada. Habilítala en la lista de apps para generar y editar el contexto.',
    saveReason: 'Motivo del cambio',
    saveHelp: 'Guardar graba el borrador completo y anula las aprobaciones ya dadas.',
    saved: 'Borrador guardado.',
    stale: 'Otra persona guardó este borrador antes que tú. Recarga para ver la versión actual; tus cambios locales se perderán.',
    reload: 'Recargar',
    issues: 'Lo que impide publicar',
    approvals: 'Aprobaciones del borrador v{{n}}',
    approvalsHelp: 'El borrador necesita la aprobación del dueño de negocio y del dueño técnico.',
    discard: 'Descartar borrador',
    discardConfirm: '¿Descartar el borrador v{{n}}? Ya no se podrá publicar.',
    discarded: 'Borrador descartado.',
    noVersions: 'Todavía no hay versiones.',
    openFrom: 'Abrir borrador desde esta versión',
    versionsTable: { number: 'Versión', state: 'Estado', by: 'Por', when: 'Cuándo', reason: 'Motivo y cambios' },
    states: { rascunho: 'Borrador', publicada: 'Publicada', arquivada: 'Archivada', descartada: 'Descartada' },
    changes: {
      when: 'Cuándo',
      who: 'Quién',
      part: 'Parte',
      operation: 'Operación',
      reason: 'Motivo',
      before: 'Antes',
      after: 'Después',
      show: 'Ver el texto',
      empty: 'No hay cambios registrados.',
    },
    operations: { add: 'Inclusión', replace: 'Cambio', remove: 'Eliminación' },
    changeClient: 'Cambiar cliente',
    setClient: 'Vincular a un cliente',
    changeClientHelp: 'La app pasa al cliente elegido; el contexto no cambia.',
    clientRemoved: 'App quitada del cliente.',
    clientChanged: 'Cliente de la app cambiado.',
    unlinkConfirm: '¿Quitar {{app}} de {{client}}?',
    unvalidated: {
      noticeBy: 'Por {{by}} el {{when}}.',
      reason: 'Motivo',
      reasonHint: 'Al menos 10 caracteres. El motivo queda en la versión, en la alerta y en la auditoría.',
      pendingTitle_one: '{{count}} pendiente',
      pendingTitle_other: '{{count}} pendientes',
      dialogTitle: 'Publicar la versión {{n}} directo',
      dialogHelp: 'El contexto llega a los agentes sin el flujo completo. Las aprobaciones que faltan quedan a tu nombre, en lugar de los dueños, y la versión queda marcada.',
      confirm: 'Publicar directo',
      open: 'Publicar directo...',
      done: 'Versión {{n}} publicada directo.',
      title: 'Publicar directo',
      help: 'Atajo del admin para cuando el cliente aún no validó o los dueños no pueden aprobar. Pide motivo, genera una alerta y queda en la auditoría.',
      normalTitle: 'Publicar con aprobación',
      normalHelp: 'El camino normal: los dos dueños aprueban el borrador y el admin publica.',
      ownerPending: {
        negocio: 'Falta la aprobación del dueño de negocio ({{owner}}).',
        tecnico: 'Falta la aprobación del dueño técnico ({{owner}}).',
      },
      adminOnly: 'Solo el administrador publica.',
      nothingPending: 'Nada pendiente. Usa Publicar.',
      refused: '{{who}} rechazó esta versión. Edita el borrador o pide una nueva decisión: el atajo no pasa por encima del rechazo.',
      nothingToPublish: 'La versión {{n}} está publicada y no hay cambios esperando. Para cambiar el contexto, edítalo en la pestaña Contexto y guarda.',
    },
  },
  users: {
    title: 'Usuarios',
    subtitle: 'Quién usa Lastrouter y con qué rol. Los roles viven en Entra ID y esta pantalla cambia Entra directamente, sin el portal de Azure. Las apps que ve cada persona vienen de Qlik.',
    addButton: 'Dar acceso a una persona',
    giveAccess: 'Dar acceso',
    search: 'Buscar',
    searchPlaceholder: 'Nombre o correo',
    roleFilter: 'Rol',
    noRole: 'Sin rol',
    caption: '{{count}} personas',
    person: 'Persona',
    role: 'Rol en Entra',
    qlikUser: 'Usuario en Qlik',
    lastSeen: 'Último acceso',
    never: 'Nunca lo usó',
    you: 'tú',
    group: 'Grupo',
    origin: { entra: 'Asignado en Entra', audit: 'Solo en la auditoría' },
    roles: { reader: 'Lector', editor: 'Editor', admin: 'Admin' },
    roleHelp: { reader: 'hace consultas dinámicas y analiza', editor: 'también propone correcciones de contexto en el chat', admin: 'también usa el panel' },
    roleOf: 'Rol de {{name}}',
    changed: '{{name}} ahora es {{role}}.',
    selfLock: 'No puedes quitarte tu propio rol de Admin. Pídeselo a otro administrador.',
    empty: 'Nadie con rol en Entra ni en la auditoría de los últimos 90 días.',
    noMatch: 'Nadie con ese nombre o rol.',
    graph: {
      not_configured: 'El panel no está conectado a Entra',
      forbidden: 'Lastrouter todavía no tiene permiso para cambiar roles en Entra',
      error: 'Entra no respondió',
      not_connected: 'Conecta tu cuenta para ver y cambiar los roles de Entra',
      connect: 'Conectar a Entra',
      ok: '',
      steps: 'Mientras tanto, la lista muestra quién aparece en la auditoría, y agregar, cambiar o quitar roles queda desactivado.',
    },
    add: {
      title: 'Dar acceso a una persona',
      help: 'Busca a la persona en el directorio de Entra y elige el rol. El cambio vale en su próximo inicio de sesión.',
      role: 'Rol',
      search: 'Nombre o correo',
      searchPlaceholder: 'maria o maria@empresa.com',
      searchButton: 'Buscar',
      noResults: 'No se encontró a nadie en el directorio.',
      give: 'Dar acceso como {{role}}',
      done: '{{name}} recibió el rol {{role}}.',
    },
    remove: {
      button: 'Quitar acceso',
      aria: 'Quitar el acceso de {{name}}',
      title: '¿Quitar el acceso de {{name}}?',
      help: 'La persona pierde los roles de Lastrouter en Entra y deja de usar el chat, Teams y el panel en el próximo login. Puedes volver a dar acceso después.',
      confirm: 'Quitar acceso',
      done: '{{name}} ya no tiene acceso.',
    },
  },
  access: {
    title: 'Acceso',
    subtitle: 'Los roles del IdP dicen qué puede hacer la persona: Lector hace consultas dinámicas, Editor también corrige el contexto desde el chat, Admin usa el panel. Las apps que ve vienen de Qlik.',
    subject: 'Tema',
    source: 'Fuente',
    owners: 'Dueños',
    simTitle: 'Simular una persona',
    simHelp: 'Muestra las herramientas que estos roles permiten y las apps que la persona ve en Qlik. Lista las apps como ella, sin consultar datos.',
    upn: 'Login (UPN)',
    roles: 'Roles (uno por línea)',
    clientApp: 'App cliente (client id)',
    simulate: 'Simular',
    channel: 'Canal',
    refused: 'Rechazado: {{reason}}',
    tools: 'Herramientas',
    subjects: 'Temas',
    sourceUser: 'Usuario en la fuente',
  },
  people: {
    title: 'Personas',
    subtitle: 'Quién usa Lastrouter, con qué rol y con qué límite.',
    tabUsers: 'Usuarios',
    tabLimits: 'Límites por persona',
    tabSimulate: 'Simular acceso',
  },
  products: {
    status: 'Estado',
    configured: 'Configurado',
    notConfigured: 'No configurado',
    fromFile: 'Hoy vale la configuración del archivo (lastrouter.yaml). Al guardar un cambio aquí, el panel pasa a valer para todos los canales, empezando por una copia del archivo.',
    fromPanel: 'La configuración de los canales está guardada en el panel y vale por encima del archivo.',
    backToFile: 'Volver a usar el archivo',
    backToFileHelp: 'La configuración guardada en el panel para todos los canales deja de valer y vuelve la del archivo. Los límites por canal siguen.',
    savedFile: 'Volvió a valer el archivo de configuración.',
    saved: 'Configuración del canal guardada.',
    channelHelp: 'Lo que este canal puede hacer con el contexto. Cambia al instante, sin reiniciar.',
    staging: 'Homologación',
    tools: 'Lo que el canal puede hacer',
    toolsHelp: 'Menos opciones dan respuestas más precisas.',
    toolsRequired: 'Elige al menos una.',
    limit: 'Consultas por minuto, por persona',
    limitHelp: 'Vacío: vale {{n}} por minuto.',
    limitHelpFile: 'Vacío: vale el límite general, en Configuración.',
    privateValues: 'Muestra los valores en la pantalla de la persona',
    privateValuesHelp: 'Para clientes en modo privacidad. Encendido: el canal muestra los números solo a quien preguntó, y la IA no los ve. Apagado: el canal recibe la respuesta sin los valores. Enciéndelo solo en Chat y en Ask en Teams, que saben ocultar los números a la IA.',
    advanced: 'Avanzado',
    channelId: 'Id del canal',
    channelIdHelp: 'Nombre corto, en minúsculas, que aparece en Auditoría y en Conversaciones.',
    idError: 'Usa letras minúsculas, números, - o _, sin repetir otro canal.',
    clientIds: 'Ids de las apps cliente en Entra (uno por línea)',
    clientIdsHelp: 'El id de la aplicación (client id) con que entra el agente. Vacío: solo Lastrouter usa este canal.',
    removeConnector: 'Quitar conector',
    removeConnectorHelp: '{{name}} deja de usar Lastrouter. Los demás canales no cambian.',
    noChannel: 'Este canal aún no tiene configuración (id "{{id}}"). Sin ella, no puede usar Lastrouter.',
    createChannel: 'Configurar',
    copy: 'Copiar',
    copied: 'Copiado.',
    teams: {
      title: 'Ask en Teams',
      subtitle: 'Ask, el asistente de datos que usa el contexto de Lastrouter, dentro de Microsoft Teams.',
      channel: 'Configuración en Lastrouter',
      lastQuestion: 'Última consulta dinámica desde Teams',
      noQuestion: 'Ninguna en los últimos días',
      package: 'Paquete de la app',
      packageHelp: 'La versión está en el paquete (zip) de la app de Teams, generado por el equipo de Cluster. Esta pantalla aún no lee la versión.',
      installTitle: 'Cómo instalar en Teams',
      install1: 'Pide al equipo de Cluster el paquete de la app de Teams (archivo .zip).',
      install2: 'En el Teams admin center, en Aplicaciones de Teams > Administrar aplicaciones, sube el paquete o actualiza la app existente.',
      install3: 'Habilita la app para quien la va a usar y pruébala con una consulta dinámica.',
      channelTitle: 'Configuración de Ask en Teams',
    },
    chat: {
      title: 'Chat',
      subtitle: 'El chat de Lastrouter en el navegador, para probar el contexto o usarlo a diario.',
      open: 'Abrir el chat',
      address: 'Dirección',
      login: 'Login con Entra',
      help: 'Quien tiene rol en Lastrouter entra con la cuenta de la organización y ve las apps que ve en Qlik.',
      off: 'El chat aún no está encendido: falta la sección chat en el archivo de configuración. Habla con TI.',
      channelTitle: 'Configuración del Chat',
    },
    mcp: {
      title: 'Conectores MCP',
      subtitle: 'Claude, Copilot y otros agentes usando el contexto de Lastrouter por el protocolo MCP.',
      add: 'Agregar conector',
      connectTitle: 'Cómo conectar',
      address: 'Dirección del MCP',
      claude1: 'En Claude, abre Configuración > Conectores y elige agregar un conector personalizado.',
      claude2: 'Pega la dirección del MCP de arriba.',
      claude3: 'Entra con la cuenta de la organización cuando Claude lo pida.',
      copilot1: 'En Copilot Studio, abre el agente y ve a Herramientas > Agregar herramienta > Model Context Protocol.',
      copilot2: 'Pega la dirección del MCP y elige el login OAuth con Entra.',
      copilot3: 'Agrega aquí un conector con el id de la aplicación de Copilot, en Avanzado.',
      newConnector: 'Nuevo conector',
      empty: 'Ningún conector. Usa "Agregar conector" para habilitar Claude, Copilot u otro agente.',
      kinds: {
        claude: 'Claude ({{id}})',
        copilot: 'Copilot ({{id}})',
        other: 'Otro conector ({{id}})',
      },
    },
  },
  limits: {
    subtitle: 'Consultas por minuto que cada persona puede hacer. Cada canal y cada persona pueden tener su límite; vale al instante, sin reiniciar.',
    global: 'Límite general por persona',
    globalHelp: 'Vacío: vale el del archivo de configuración.',
    perUser: 'Excepciones por persona',
    perUserHelp: 'Para quien necesita un límite distinto del estándar. Vale al instante.',
    limit: 'Consultas por minuto',
    user: 'Login',
    addUser: 'Agregar excepción',
    noUsers: 'Ninguna excepción.',
    saved: 'Límites guardados.',
    invalid: 'Usa un número entero de 1 a 10.000.',
  },
  quality: {
    title: 'Calidad',
    subtitle: 'Cómo está respondiendo el agente, por tema y por semana.',
    tabWeekly: 'Tasa de respuesta',
    tabFeedback: 'Comentarios',
    tabProposals: 'Propuestas',
    tabCandidates: 'Preguntas para la suite',
    tabReports: 'Resultados de la suite',
    chartTitle: 'Tasa de respuesta por semana',
    chartHelp: 'Parte de las consultas dinámicas respondidas sin pedir aclaración. Las semanas con marcador cayeron más de 10 puntos y piden revisión con el dueño del tema.',
    showTable: 'Ver tabla',
    showChart: 'Ver gráfico',
    week: 'Semana',
    subject: 'Tema',
    questions: 'Consultas dinámicas',
    answered: 'Respondidas',
    clarifications: 'Aclaraciones',
    noAnswer: 'Sin respuesta',
    errors: 'Errores',
    negative: 'Comentarios negativos',
    rate: 'Tasa de respuesta',
    review: 'Revisar',
    noSubject: 'Sin tema',
    noData: 'Aún no hay consultas dinámicas registradas.',
    feedback: {
      when: 'Cuándo',
      kind: 'Tipo',
      comment: 'Comentario',
      query: 'Consulta',
      state: 'Estado',
      routed: 'Enviado a',
      kinds: { wrong: 'Respuesta incorrecta', review: 'Pedido de revisión', useful: 'Útil' },
      states: { aberto: 'Abierto', em_revisao: 'En revisión', resolvido: 'Resuelto', descartado: 'Descartado' },
      change: 'Cambiar estado',
      resolution: 'Resolución',
      empty: 'No hay comentarios con este filtro.',
      saved: 'Estado actualizado.',
    },
    proposals: {
      generate: 'Generar propuestas',
      generated: 'Propuestas generadas: {{count}}.',
      kinds: { sinonimo: 'Sinónimo', exemplo: 'Ejemplo', nova_query: 'Consulta nueva', revisao: 'Revisión' },
      states: { proposta: 'Propuesta', aceita: 'Aceptada', recusada: 'Rechazada' },
      accept: 'Aceptar',
      refuse: 'Rechazar',
      evidence: 'Evidencia',
      acceptHelp: 'Aceptar no cambia el catálogo: abre un borrador con el cambio.',
      empty: 'No hay propuestas. Genéralas a partir de las conversaciones con problema.',
    },
    candidates: {
      question: 'Pregunta',
      reason: 'Por qué',
      empty: 'No hay preguntas candidatas.',
      help: 'Preguntas reales sin respuesta o con comentarios negativos. Lleva las relevantes a perguntas.yaml con la respuesta correcta por usuario.',
    },
    reports: {
      label: 'Ejecución',
      when: 'Cuándo',
      answerRate: 'Respuesta',
      accuracy: 'Acierto',
      certifiedWrong: 'Certificadas incorrectas',
      deterministic: 'Determinística',
      open: 'Abrir informe',
      empty: 'Aún no hay resultados de la suite. Ejecuta lastrouter eval.',
    },
  },
  channels: {
    toolNames: {
      search_context: 'Buscar en el contexto',
      list_values: 'Listar los valores de un campo',
      run_validated_query: 'Ejecutar consulta validada',
      explain_result: 'Explicar un resultado',
      resolve_question: 'Entender la consulta',
      report_feedback: 'Registrar la opinión de la persona',
      query_metric: 'Consultar métrica',
      list_apps: 'Listar las apps',
      describe_app: 'Describir una app',
      query: 'Consultar datos',
      propose_context_change: 'Proponer corrección de contexto',
    },
  },
  conversations: {
    find: 'Buscar en las conversaciones',
    findPlaceholder: 'Consulta, respuesta o persona',
    noMatch: 'Ninguna conversación cargada con "{{q}}".',
    title: 'Conversaciones',
    subtitle: 'Las consultas dinámicas que hicieron las personas, cómo se respondieron y su retorno. Los valores de los resultados no se guardan aquí.',
    defaultPeriod: 'Sin fecha, muestra los últimos 7 días.',
    question: 'Consulta dinámica',
    answer: 'Respuesta',
    feedback: 'Retorno',
    useful: 'Útil',
    wrong: 'Incorrecta',
    noFeedback: 'Sin retorno',
    ok: 'Respondida',
    detail: 'Conversación',
    noQuestion: 'La llamada no trajo la consulta.',
    auditOnly: 'Este canal no tiene bot propio: Lastrouter ve las llamadas a herramientas, no el texto de la respuesta.',
    auditShort: 'El texto de la respuesta queda en el cliente',
    model: 'Modelo',
    rounds: 'Rondas del LLM',
    tokens: 'Tokens del LLM',
    tokensValue: '{{input}} de entrada ({{cached}} en caché), {{output}} de salida, en {{calls}} llamadas',
    unverified: 'Números sin fuente',
    steps: 'Pasos',
    noSteps: 'No se llamó ninguna herramienta.',
    results: 'Resultados mostrados',
    resultsGone: '{{count}} resultado(s) fuera de la retención o de una app que no ves.',
    filters: 'Filtros',
    noFilters: 'ninguno',
    except: 'excepto',
    range: 'del {{from}} al {{to}}',
    certified: 'Certificado',
    notCertified: 'No certificado',
    executedAt: 'Calculado el',
    caveats: 'Avisos',
    replay: 'Repetir la consulta',
    empty: 'Ninguna conversación con estos filtros.',
    shown: '{{count}} conversación(es)',
    truncated: 'Mostrando las {{count}} más recientes. Acorta el período para ver las demás.',
  },
  audit: {
    title: 'Auditoría',
    subtitle: 'Una línea por llamada de herramienta, con la expresión ejecutada. Sin valores de resultado.',
    from: 'Desde',
    to: 'Hasta',
    user: 'Usuario',
    tool: 'Herramienta',
    status: 'Estado',
    subject: 'Tema',
    any: 'Todos',
    filter: 'Filtrar',
    clear: 'Limpiar',
    when: 'Cuándo',
    channel: 'Canal',
    query: 'Consulta',
    rows: 'Filas',
    duration: 'Duración',
    statuses: { ok: 'Respondida', clarification: 'Aclaración', no_answer: 'Sin respuesta', error: 'Error' },
    beforeTool: 'Rechazada antes de la herramienta',
    loadMore: 'Cargar más',
    export: 'Exportar CSV',
    verify: 'Revisar cadena de hash',
    chainOk: 'Cadena íntegra: {{records}} líneas en {{files}} archivo(s).',
    chainBroken: 'Cadena rota en {{file}}, línea {{line}}: {{reason}}',
    detail: 'Línea de auditoría',
    empty: 'No hay líneas con estos filtros.',
    shown: '{{count}} línea(s) en pantalla',
    expressions: 'Expresiones ejecutadas',
    publication: 'Publicación del catálogo',
    promptModel: 'Prompt y modelo',
    versions: 'Versiones',
    lastReload: 'Última recarga de la app',
    tokens: 'Tokens (entrada / salida)',
    result: 'Resultado',
    hash: 'Hash de la cadena',
  },
  appearance: {
    title: 'Apariencia',
    subtitle: 'Solo cambia la capa de marca: nombre del asistente, color de acento, logo e ícono. La estructura y la tipografía son de Cluster.',
    name: 'Nombre del asistente',
    voice: 'Tono de voz',
    voiceHelp: 'Cómo habla el asistente en todos los canales y apps. Una app con la sección Tono de voz en su contexto usa el suyo.',
    voicePlaceholder: 'Ej.: formal y directo, sin jerga, explique las siglas la primera vez.',
    color: 'Color de acento',
    colorHelp: 'Necesita 4,5:1 con texto blanco y 3:1 sobre el fondo oscuro.',
    onWhite: 'Texto blanco sobre el color: {{ratio}}:1',
    onDark: 'Color sobre el fondo oscuro: {{ratio}}:1',
    passes: 'Contraste aprobado.',
    fails: 'Este color no pasa el contraste. Elige uno más oscuro.',
    invalidColor: 'Usa el formato #RRGGBB.',
    logo: 'Logo',
    icon: 'Ícono',
    upload: 'Subir imagen',
    removeImage: 'Quitar',
    imageHelp: 'PNG, JPEG o WebP hasta 200 KB.',
    tooLarge: 'La imagen supera los 200 KB.',
    wrongType: 'Usa PNG, JPEG o WebP.',
    preview: 'Vista previa de una respuesta',
    previewQuestion: '¿Cuánto recaudó Porto Alegre en septiembre del año pasado?',
    previewAnswer: '1.234.567,89 BRL en septiembre de 2025, Porto Alegre',
    certified: 'Certificado',
    useful: 'Útil',
    wrong: 'Incorrecto',
    askReview: 'Pedir revisión',
    saved: 'Apariencia guardada.',
  },
  settings: {
    title: 'Configuración',
    subtitle: 'Límite general de uso e información del sistema. El archivo de configuración viene de lastrouter.yaml y solo TI lo cambia.',
    version: 'Versión',
    edition: 'Edición',
    instance: 'Instancia',
    tenant: 'Tenant',
    backend: 'Estado guardado en',
    uptime: 'En línea hace',
    license: 'Licencia',
    customer: 'Cliente',
    validity: 'Validez',
    features: 'Recursos',
    lastCheckin: 'Último check-in',
    config: 'Archivo de configuración (sin secretos)',
    editions: { selfhosted: 'Self-hosted', cloud: 'Cloud' },
    backends: { files: 'Archivos', postgres: 'PostgreSQL' },
  },
};
